// ccw 2026-09-28 msl-map: Inline SVG US map colored by MSL territory, with hover tooltip.
// The SVG is aria-hidden: the liaison cards are the accessible equivalent, so states are not
// Tab stops. Focus is applied with classes (has-focus / is-focused / is-hover), not inline styles.
import React, { useRef } from 'react';

import { MSL_MAP_VIEWBOX } from '../constants/mslStates';
import { MSL_UNASSIGNED_COLOR, MSL_HIT_TARGETS } from '../constants/mslTerritories';

const TOOLTIP_WIDTH = 240
const TOOLTIP_OFFSET = 16
const TOOLTIP_RISE = 70

const stateFromEvent = (e) => {
	const el = e.target && e.target.closest ? e.target.closest('[data-state]') : null
	return el ? el.getAttribute('data-state') : null
}

const MslMap = (props) => {
	const { states, territoryById, stateTerritory, focusedTerritoryIds, hasFocus, stateInfo, isTouch, fncStateFocus, fncMapClear } = props
	const wrapRef = useRef(null)
	const tipRef = useRef(null)

	// Tooltip follows the cursor, 16px away, flipped left near the right edge. Positioned via
	// the ref so mouse movement within a state doesn't re-render the map.
	const fncPositionTip = (e) => {
		if (!wrapRef.current || !tipRef.current) return
		const rect = wrapRef.current.getBoundingClientRect()
		let x = e.clientX - rect.left + TOOLTIP_OFFSET
		if (x + TOOLTIP_WIDTH > rect.width) x = e.clientX - rect.left - TOOLTIP_OFFSET - TOOLTIP_WIDTH
		const y = e.clientY - rect.top - TOOLTIP_RISE
		tipRef.current.style.left = Math.max(0, x) + 'px'
		tipRef.current.style.top = Math.max(0, y) + 'px'
	}

	const onMouseMove = (e) => {
		if (isTouch) return
		const stateId = stateFromEvent(e)
		if (!stateId) {
			if (stateInfo) fncMapClear()
			return
		}
		fncStateFocus(stateId)
		fncPositionTip(e)
	}

	const onMouseLeave = () => {
		if (!isTouch) fncMapClear()
	}

	const onClick = (e) => {
		if (!isTouch) return
		const stateId = stateFromEvent(e)
		stateId ? fncStateFocus(stateId) : fncMapClear()
	}

	// Draw the hovered state last so its outline isn't covered by neighbours; hit circles go
	// on top of everything.
	const hoveredId = stateInfo ? stateInfo.stateId : null
	const orderedStates = hoveredId ? [...states.filter(s => s.id !== hoveredId), ...states.filter(s => s.id === hoveredId)] : states

	return (
		<div className="msl-map-wrap" ref={wrapRef}>
			<svg
				className={'msl-map' + (hasFocus ? ' has-focus' : '')}
				viewBox={MSL_MAP_VIEWBOX}
				aria-hidden="true"
				focusable="false"
				onMouseMove={onMouseMove}
				onMouseLeave={onMouseLeave}
				onClick={onClick}
			>
				{orderedStates.map(s => {
					const territoryId = stateTerritory[s.id]
					const className = [
						territoryId ? '' : 'unassigned',
						territoryId && focusedTerritoryIds.indexOf(territoryId) > -1 ? 'is-focused' : '',
						s.id === hoveredId ? 'is-hover' : '',
					].filter(Boolean).join(' ')
					return (
						<path
							key={s.id}
							id={'state-' + s.id}
							data-state={s.id}
							d={s.d}
							fill={territoryId ? territoryById[territoryId].color : MSL_UNASSIGNED_COLOR}
							className={className || undefined}
						/>
					)
				})}
				{states.filter(s => MSL_HIT_TARGETS[s.id]).map(s => {
					const hit = MSL_HIT_TARGETS[s.id]
					return <circle key={'hit-' + s.id} className="msl-hit" data-state={s.id} cx={hit.cx !== undefined ? hit.cx : s.cx} cy={hit.cy !== undefined ? hit.cy : s.cy} r={hit.r} />
				})}
			</svg>

			{/* Always mounted (hidden when empty) so the first mousemove can position it. */}
			<div className="msl-tooltip" ref={tipRef} aria-hidden="true" style={{ display: !isTouch && stateInfo ? 'block' : 'none' }}>
				{stateInfo &&
					<>
						<b>{stateInfo.name}</b>
						<div>
							<span className="msl-dot" style={{ background: stateInfo.color }}></span>
							<span>{stateInfo.text}</span>
						</div>
					</>
				}
			</div>
		</div>
	)
}

export default MslMap
